import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { differenceInYears, parseISO } from 'date-fns';
import { Loader2, Sparkles, Plus, Trash2, Search, UserCheck, History, AlertTriangle, ChevronDown, ChevronUp, Calendar, Stethoscope } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Alert, AlertDescription, AlertTitle,
} from '../ui/alert';
import { toast } from 'sonner';
import {
  API_URL, PAYMENT_METHODS, OJO_OPTIONS, DISCOUNT_TYPES, 
  INSTITUCION_OPTIONS, initialExamItem,
  formatCurrency, calculateTotal, calculateSubtotal
} from './constants';

const PatientForm = ({ 
  isOpen, 
  onClose, 
  editingPatient, 
  formData, 
  setFormData,
  onSave,
  saving,
  token,
  linkedAppointmentId,
  examTypeNames = [],  // Dynamic exam type names for datalist
  examTypesData = []   // Full exam types with prices
}) => {
  const [doctorSuggestions, setDoctorSuggestions] = useState([]);
  const [showDoctorSuggestions, setShowDoctorSuggestions] = useState(false);
  const doctorInputRef = useRef(null);
  
  // Patient search/autocomplete state
  const [patientSearch, setPatientSearch] = useState('');
  const [patientSuggestions, setPatientSuggestions] = useState([]);
  const [showPatientSuggestions, setShowPatientSuggestions] = useState(false);
  const [searchingPatients, setSearchingPatients] = useState(false);
  const [selectedExistingPatient, setSelectedExistingPatient] = useState(null);
  const patientInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  // Visit history state
  const [patientHistory, setPatientHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  
  // Duplicate warning state
  const [potentialDuplicates, setPotentialDuplicates] = useState([]);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  useEffect(() => {
    fetchDoctorSuggestions();
  }, []);
  
  // Reset patient search when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setPatientSearch('');
      setPatientSuggestions([]);
      setSelectedExistingPatient(null);
      setPatientHistory([]);
      setShowHistoryPanel(false);
      setPotentialDuplicates([]);
      setShowDuplicateWarning(false);
      // Pre-fill search with name if coming from appointment
      if (formData.nombre && !editingPatient) {
        setPatientSearch(formData.nombre);
        searchExistingPatients(formData.nombre);
      }
    }
  }, [isOpen]);
  
  // Fetch patient's visit history
  const fetchPatientHistory = async (patientName, patientPhone) => {
    if (!patientName) return;
    
    setLoadingHistory(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/patients/history?nombre=${encodeURIComponent(patientName)}&telefono=${encodeURIComponent(patientPhone || '')}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPatientHistory(response.data || []);
      if (response.data && response.data.length > 0) {
        setShowHistoryPanel(true);
      }
    } catch (error) {
      console.error('Error fetching patient history:', error);
      setPatientHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };
  
  // Check for potential duplicates when phone number changes
  const checkForDuplicates = async (nombre, telefono) => {
    if (editingPatient) return; // Don't check when editing
    if (!nombre || nombre.length < 3) {
      setPotentialDuplicates([]);
      setShowDuplicateWarning(false);
      return;
    }
    
    try {
      const response = await axios.get(
        `${API_URL}/api/patients/check-duplicate?nombre=${encodeURIComponent(nombre)}&telefono=${encodeURIComponent(telefono || '')}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data && response.data.length > 0 && !selectedExistingPatient) {
        setPotentialDuplicates(response.data);
        setShowDuplicateWarning(true);
      } else {
        setPotentialDuplicates([]);
        setShowDuplicateWarning(false);
      }
    } catch (error) {
      console.error('Error checking duplicates:', error);
    }
  };
  
  // Check for duplicates when phone changes
  useEffect(() => {
    if (formData.telefono && formData.telefono.length >= 4 && formData.nombre && !selectedExistingPatient) {
      const timer = setTimeout(() => {
        checkForDuplicates(formData.nombre, formData.telefono);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [formData.telefono, formData.nombre]);
  
  // Search for existing patients as user types
  const searchExistingPatients = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setPatientSuggestions([]);
      return;
    }
    
    setSearchingPatients(true);
    try {
      const response = await axios.get(`${API_URL}/api/patients/search?q=${encodeURIComponent(searchTerm)}&limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPatientSuggestions(response.data || []);
    } catch (error) {
      console.error('Error searching patients:', error);
      setPatientSuggestions([]);
    } finally {
      setSearchingPatients(false);
    }
  };
  
  // Handle patient search input change with debounce
  const handlePatientSearchChange = (e) => {
    const value = e.target.value;
    setPatientSearch(value);
    setSelectedExistingPatient(null);
    
    // Update the nombre field in real-time
    setFormData(prev => ({ ...prev, nombre: value }));
    
    // Debounce the search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      searchExistingPatients(value);
      setShowPatientSuggestions(true);
    }, 300);
  };
  
  // Auto-fill form with existing patient data
  const handleSelectExistingPatient = (patient) => {
    setSelectedExistingPatient(patient);
    setPatientSearch(patient.nombre);
    setShowPatientSuggestions(false);
    setPotentialDuplicates([]);
    setShowDuplicateWarning(false);
    
    // Auto-fill all patient data (keep current fecha and examenes)
    setFormData(prev => ({
      ...prev,
      nombre: patient.nombre || '',
      fecha_nacimiento: patient.fecha_nacimiento || '',
      edad: patient.edad?.toString() || '',
      telefono: patient.telefono || '',
      correo: patient.correo || '',
      nit: patient.nit || '',
      medico_referente: patient.medico_referente || prev.medico_referente || '',
      institucion: patient.institucion || prev.institucion || '',
      // IGSS fields
      igss_afiliacion: patient.igss_afiliacion || '',
      igss_codigo: patient.igss_codigo || '',
      igss_nog: patient.igss_nog || '',
      igss_spg: patient.igss_spg || '',
      igss_unidad_referente: patient.igss_unidad_referente || '',
    }));
    
    // Fetch patient's visit history
    fetchPatientHistory(patient.nombre, patient.telefono);
    
    toast.success(`Datos de ${patient.nombre} cargados automáticamente`);
  };

  // Initialize examenes array if empty
  useEffect(() => {
    if (isOpen && (!formData.examenes || formData.examenes.length === 0)) {
      // If editing and has legacy data, migrate to examenes
      if (editingPatient && formData.tipo_examen) {
        setFormData(prev => ({
          ...prev,
          examenes: [{
            tipo_examen: prev.tipo_examen || '',
            ojo_estudiar: prev.ojo_estudiar || '',
            precio: prev.precio || ''
          }]
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          examenes: [{ ...initialExamItem }]
        }));
      }
    }
  }, [isOpen, editingPatient]);

  const fetchDoctorSuggestions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/doctors/autocomplete`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDoctorSuggestions(response.data || []);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBirthDateChange = (e) => {
    const birthDate = e.target.value;
    setFormData(prev => {
      const newData = { ...prev, fecha_nacimiento: birthDate };
      if (birthDate) {
        try {
          const age = differenceInYears(new Date(), parseISO(birthDate));
          newData.edad = age.toString();
        } catch {
          // Invalid date
        }
      }
      return newData;
    });
  };

  const handleDoctorSelect = (doctor) => {
    setFormData(prev => ({ ...prev, medico_referente: doctor }));
    setShowDoctorSuggestions(false);
  };

  // Helper to get base price from exam types data
  const getBasePrice = (examTypeName) => {
    const matchedExamType = examTypesData.find(
      et => et.name.toLowerCase() === examTypeName.toLowerCase()
    );
    return matchedExamType?.default_price || 0;
  };

  // Exam handlers
  const handleExamChange = (index, field, value) => {
    setFormData(prev => {
      const newExamenes = [...(prev.examenes || [])];
      const currentExam = newExamenes[index];
      const previousOjo = currentExam.ojo_estudiar;
      
      newExamenes[index] = { ...currentExam, [field]: value };
      
      // Auto-fill price when exam type is selected/changed
      if (field === 'tipo_examen' && value) {
        const basePrice = getBasePrice(value);
        if (basePrice > 0) {
          // Only auto-fill if the current price is empty or 0
          const currentPrice = newExamenes[index].precio;
          if (!currentPrice || currentPrice === '' || currentPrice === '0' || currentPrice === 0) {
            // If "Ambos Ojos" is already selected, multiply by 2
            const multiplier = newExamenes[index].ojo_estudiar === 'AO' ? 2 : 1;
            newExamenes[index].precio = (basePrice * multiplier).toString();
          }
        }
      }
      
      // Handle eye selection change - multiply/divide price by 2 for "Ambos Ojos"
      if (field === 'ojo_estudiar') {
        const tipoExamen = newExamenes[index].tipo_examen;
        const basePrice = getBasePrice(tipoExamen);
        
        if (basePrice > 0) {
          // Changing TO "Ambos Ojos" - multiply by 2
          if (value === 'AO' && previousOjo !== 'AO') {
            const currentPrice = parseFloat(newExamenes[index].precio) || basePrice;
            // Only multiply if current price equals base price (not manually modified)
            if (currentPrice === basePrice) {
              newExamenes[index].precio = (basePrice * 2).toString();
            }
          }
          // Changing FROM "Ambos Ojos" to single eye - divide by 2
          else if (previousOjo === 'AO' && value !== 'AO') {
            const currentPrice = parseFloat(newExamenes[index].precio) || 0;
            // Only divide if current price equals double base price
            if (currentPrice === basePrice * 2) {
              newExamenes[index].precio = basePrice.toString();
            }
          }
        }
      }
      
      return { ...prev, examenes: newExamenes };
    });
  };

  const addExam = () => {
    setFormData(prev => ({
      ...prev,
      examenes: [...(prev.examenes || []), { ...initialExamItem }]
    }));
  };

  const removeExam = (index) => {
    if (formData.examenes.length <= 1) {
      toast.error('Debe haber al menos un examen');
      return;
    }
    setFormData(prev => ({
      ...prev,
      examenes: prev.examenes.filter((_, i) => i !== index)
    }));
  };

  const filteredDoctors = doctorSuggestions.filter(d =>
    d.toLowerCase().includes((formData.medico_referente || '').toLowerCase())
  );

  const subtotal = calculateSubtotal(formData.examenes);
  // Si es Cortesía, el total es 0
  const total = formData.forma_pago === 'Cortesía' 
    ? 0 
    : calculateTotal(null, formData.tipo_descuento, formData.descuento, formData.examenes);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="patient-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#1a5192]" />
            {editingPatient ? 'Editar Paciente' : 'Nuevo Registro de Paciente'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={onSave} className="space-y-6">
          {/* Basic Info Section */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-4">
            <h3 className="font-medium text-slate-700 text-sm uppercase tracking-wide">Información Personal</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fecha">Fecha de Registro *</Label>
                <Input
                  id="fecha"
                  name="fecha"
                  type="date"
                  value={formData.fecha}
                  onChange={handleInputChange}
                  className="bg-white"
                  data-testid="patient-fecha"
                />
              </div>
              
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="nombre">Nombre Completo *</Label>
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      ref={patientInputRef}
                      id="nombre"
                      value={editingPatient ? formData.nombre : patientSearch}
                      onChange={editingPatient ? handleInputChange : handlePatientSearchChange}
                      name="nombre"
                      onFocus={() => !editingPatient && patientSuggestions.length > 0 && setShowPatientSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowPatientSuggestions(false), 200)}
                      placeholder={editingPatient ? "Nombre completo" : "Buscar paciente existente o escribir nuevo..."}
                      className="bg-white pl-10 pr-10"
                      data-testid="patient-nombre"
                      autoComplete="off"
                    />
                    {searchingPatients && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-blue-500" />
                    )}
                    {selectedExistingPatient && !searchingPatients && (
                      <UserCheck className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
                    )}
                  </div>
                  
                  {/* Patient suggestions dropdown */}
                  {showPatientSuggestions && patientSuggestions.length > 0 && !editingPatient && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      <div className="px-3 py-2 text-xs font-medium text-slate-500 bg-slate-50 border-b">
                        Pacientes encontrados ({patientSuggestions.length})
                      </div>
                      {patientSuggestions.map((patient, index) => (
                        <div
                          key={patient.id || index}
                          className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0"
                          onClick={() => handleSelectExistingPatient(patient)}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-slate-900">{patient.nombre}</span>
                            {patient.telefono && (
                              <span className="text-xs text-slate-500">{patient.telefono}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                            {patient.nit && <span>NIT: {patient.nit}</span>}
                            {patient.institucion && <span>• {patient.institucion}</span>}
                            {patient.fecha && <span>• Última visita: {patient.fecha}</span>}
                          </div>
                        </div>
                      ))}
                      <div className="px-3 py-2 text-xs text-slate-400 bg-slate-50 border-t">
                        Haz clic para auto-rellenar datos o sigue escribiendo para nuevo paciente
                      </div>
                    </div>
                  )}
                </div>
                {selectedExistingPatient && (
                  <p className="text-xs text-emerald-600 flex items-center gap-1">
                    <UserCheck className="w-3 h-3" />
                    Paciente recurrente - datos cargados automáticamente
                  </p>
                )}
              </div>
              
              {/* Duplicate Warning */}
              {showDuplicateWarning && potentialDuplicates.length > 0 && !selectedExistingPatient && (
                <div className="sm:col-span-2 md:col-span-3">
                  <Alert className="bg-amber-50 border-amber-300">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <AlertTitle className="text-amber-800 font-medium">Posible paciente duplicado</AlertTitle>
                    <AlertDescription className="text-amber-700">
                      <p className="mb-2">Se encontraron pacientes con datos similares:</p>
                      <div className="space-y-2">
                        {potentialDuplicates.map((dup, idx) => (
                          <div 
                            key={idx} 
                            className="flex items-center justify-between bg-white rounded p-2 border border-amber-200"
                          >
                            <div>
                              <span className="font-medium">{dup.nombre}</span>
                              {dup.telefono && <span className="text-sm ml-2">Tel: {dup.telefono}</span>}
                              {dup.fecha && <span className="text-sm text-slate-500 ml-2">Última visita: {dup.fecha}</span>}
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => handleSelectExistingPatient(dup)}
                              className="text-amber-700 border-amber-400 hover:bg-amber-100"
                            >
                              Usar este paciente
                            </Button>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs mt-2 text-amber-600">
                        Si es un paciente nuevo, puede ignorar esta advertencia y continuar.
                      </p>
                    </AlertDescription>
                  </Alert>
                </div>
              )}
              
              {/* Patient Visit History Panel */}
              {showHistoryPanel && patientHistory.length > 0 && (
                <div className="sm:col-span-2 md:col-span-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setShowHistoryPanel(!showHistoryPanel)}
                      className="w-full px-4 py-3 flex items-center justify-between text-left bg-blue-100 hover:bg-blue-150"
                    >
                      <div className="flex items-center gap-2">
                        <History className="w-4 h-4 text-blue-600" />
                        <span className="font-medium text-blue-800">Historial de Visitas ({patientHistory.length})</span>
                      </div>
                      {showHistoryPanel ? (
                        <ChevronUp className="w-4 h-4 text-blue-600" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-blue-600" />
                      )}
                    </button>
                    
                    {loadingHistory ? (
                      <div className="p-4 flex items-center justify-center">
                        <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                      </div>
                    ) : (
                      <div className="max-h-48 overflow-y-auto">
                        {patientHistory.map((visit, idx) => (
                          <div 
                            key={visit.id || idx} 
                            className={`px-4 py-3 ${idx % 2 === 0 ? 'bg-white' : 'bg-blue-50/50'} border-b border-blue-100 last:border-0`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-3.5 h-3.5 text-blue-500" />
                                  <span className="font-medium text-slate-800">{visit.fecha}</span>
                                </div>
                                <div className="mt-1 flex items-center gap-2 text-sm text-slate-600">
                                  <Stethoscope className="w-3.5 h-3.5 text-slate-400" />
                                  {visit.examenes && visit.examenes.length > 0 ? (
                                    <span>{visit.examenes.map(e => e.tipo_examen).join(', ')}</span>
                                  ) : (
                                    <span>{visit.tipo_examen || 'Sin especificar'}</span>
                                  )}
                                </div>
                                {visit.medico_referente && (
                                  <p className="text-xs text-slate-500 mt-1">
                                    Referido por: {visit.medico_referente}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <Badge variant="outline" className="text-blue-700 border-blue-300">
                                  {formatCurrency(visit.total || visit.precio || 0)}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="fecha_nacimiento">Fecha de Nacimiento</Label>
                <Input
                  id="fecha_nacimiento"
                  name="fecha_nacimiento"
                  type="date"
                  value={formData.fecha_nacimiento}
                  onChange={handleBirthDateChange}
                  className="bg-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edad">Edad *</Label>
                <Input
                  id="edad"
                  name="edad"
                  type="number"
                  min="0"
                  max="150"
                  value={formData.edad}
                  onChange={handleInputChange}
                  placeholder="Años"
                  className="bg-white"
                  data-testid="patient-edad"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="telefono">Teléfono *</Label>
                <Input
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleInputChange}
                  placeholder="5555-1234"
                  className="bg-white"
                  data-testid="patient-telefono"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="correo">Correo Electrónico</Label>
                <Input
                  id="correo"
                  name="correo"
                  type="email"
                  value={formData.correo}
                  onChange={handleInputChange}
                  placeholder="correo@ejemplo.com"
                  className="bg-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="nit">NIT</Label>
                <Input
                  id="nit"
                  name="nit"
                  value={formData.nit}
                  onChange={handleInputChange}
                  placeholder="12345678-9"
                  className="bg-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Institución</Label>
                <Select
                  value={formData.institucion}
                  onValueChange={(value) => handleSelectChange('institucion', value)}
                >
                  <SelectTrigger className="bg-white" data-testid="patient-institucion">
                    <SelectValue placeholder="Seleccionar institución" />
                  </SelectTrigger>
                  <SelectContent>
                    {INSTITUCION_OPTIONS.map((inst) => (
                      <SelectItem key={inst} value={inst}>{inst}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* IGSS Conditional Fields */}
            {formData.institucion === 'IGSS' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 pt-3 border-t border-slate-200">
                <div className="space-y-2">
                  <Label htmlFor="igss_afiliacion">Afiliación</Label>
                  <Input
                    id="igss_afiliacion"
                    name="igss_afiliacion"
                    value={formData.igss_afiliacion}
                    onChange={handleInputChange}
                    placeholder="Número de afiliación"
                    className="bg-white"
                    data-testid="patient-igss-afiliacion"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="igss_codigo">Código IGSS</Label>
                  <Input
                    id="igss_codigo"
                    name="igss_codigo"
                    value={formData.igss_codigo}
                    onChange={handleInputChange}
                    placeholder="Código IGSS"
                    className="bg-white"
                    data-testid="patient-igss-codigo"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="igss_nog">NOG (Número de Orden)</Label>
                  <Input
                    id="igss_nog"
                    name="igss_nog"
                    value={formData.igss_nog}
                    onChange={handleInputChange}
                    placeholder="Número de orden"
                    className="bg-white"
                    data-testid="patient-igss-nog"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="igss_spg">SPS-465</Label>
                  <Input
                    id="igss_spg"
                    name="igss_spg"
                    value={formData.igss_spg}
                    onChange={handleInputChange}
                    placeholder="SPS-465"
                    className="bg-white"
                    data-testid="patient-igss-spg"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="igss_unidad_referente">Unidad Referente</Label>
                  <Input
                    id="igss_unidad_referente"
                    name="igss_unidad_referente"
                    value={formData.igss_unidad_referente}
                    onChange={handleInputChange}
                    placeholder="Unidad que refiere"
                    className="bg-white"
                    data-testid="patient-igss-unidad"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Exams Section - Multiple Exams */}
          <div className="bg-blue-50 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-blue-700 text-sm uppercase tracking-wide">
                Exámenes ({formData.examenes?.length || 0})
              </h3>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addExam}
                className="border-blue-300 text-blue-600 hover:bg-blue-100"
                data-testid="add-exam-button"
              >
                <Plus className="w-4 h-4 mr-1" />
                Agregar Examen
              </Button>
            </div>

            {/* Exam Items */}
            <div className="space-y-3">
              {(formData.examenes || []).map((exam, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-lg p-3 border border-blue-200 relative"
                  data-testid={`exam-item-${index}`}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
                    <div className="sm:col-span-5 space-y-2">
                      <Label>Tipo de Examen *</Label>
                      <Input
                        value={exam.tipo_examen}
                        onChange={(e) => handleExamChange(index, 'tipo_examen', e.target.value)}
                        placeholder="OCT Macular, Campimetría, etc."
                        className="bg-slate-50"
                        list={`exam-types-${index}`}
                        data-testid={`exam-tipo-${index}`}
                      />
                      <datalist id={`exam-types-${index}`}>
                        {examTypeNames.map((type) => (
                          <option key={type} value={type} />
                        ))}
                      </datalist>
                    </div>
                    
                    <div className="sm:col-span-3 space-y-2">
                      <Label>Ojo a Estudiar</Label>
                      <Select
                        value={exam.ojo_estudiar}
                        onValueChange={(value) => handleExamChange(index, 'ojo_estudiar', value)}
                      >
                        <SelectTrigger className="bg-slate-50" data-testid={`exam-ojo-${index}`}>
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent>
                          {OJO_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="sm:col-span-3 space-y-2">
                      <Label>Precio (Q) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={exam.precio}
                        onChange={(e) => handleExamChange(index, 'precio', e.target.value)}
                        placeholder="0.00"
                        className="bg-slate-50"
                        data-testid={`exam-precio-${index}`}
                      />
                    </div>
                    
                    <div className="sm:col-span-2 space-y-2">
                      <Label>Duración (min)</Label>
                      <Input
                        type="number"
                        min="5"
                        step="5"
                        value={exam.duracion || ''}
                        onChange={(e) => handleExamChange(index, 'duracion', e.target.value)}
                        placeholder="30"
                        className="bg-slate-50"
                        data-testid={`exam-duracion-${index}`}
                      />
                    </div>
                    
                    <div className="sm:col-span-1 flex justify-end items-end">
                      {formData.examenes.length > 1 && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => removeExam(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`remove-exam-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Subtotal */}
            <div className="flex justify-end pt-2 border-t border-blue-200">
              <div className="bg-white rounded-lg px-4 py-2 inline-flex items-center gap-3">
                <span className="text-sm text-blue-700">Subtotal Exámenes:</span>
                <span className="text-lg font-bold text-blue-800">{formatCurrency(subtotal)}</span>
              </div>
            </div>

            {/* Referring Doctor */}
            <div className="pt-3 border-t border-blue-200">
              <div className="space-y-2 relative max-w-sm">
                <Label htmlFor="medico_referente">Médico Referente</Label>
                <Input
                  ref={doctorInputRef}
                  id="medico_referente"
                  name="medico_referente"
                  value={formData.medico_referente}
                  onChange={(e) => {
                    handleInputChange(e);
                    setShowDoctorSuggestions(true);
                  }}
                  onFocus={() => setShowDoctorSuggestions(true)}
                  placeholder="Dr. Juan Pérez"
                  className="bg-white"
                  autoComplete="off"
                />
                {showDoctorSuggestions && filteredDoctors.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredDoctors.map((doctor, idx) => (
                      <div
                        key={idx}
                        className="px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm"
                        onClick={() => handleDoctorSelect(doctor)}
                      >
                        {doctor}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payment Section */}
          <div className="bg-emerald-50 rounded-lg p-4 space-y-4">
            <h3 className="font-medium text-emerald-700 text-sm uppercase tracking-wide">Información de Pago</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Tipo de Descuento</Label>
                <Select
                  value={formData.tipo_descuento}
                  onValueChange={(value) => handleSelectChange('tipo_descuento', value)}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DISCOUNT_TYPES.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {formData.tipo_descuento !== 'sin_descuento' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="descuento">
                      {formData.tipo_descuento === 'porcentaje' ? 'Descuento (%)' : 'Descuento (Q)'}
                    </Label>
                    <Input
                      id="descuento"
                      name="descuento"
                      type="number"
                      step={formData.tipo_descuento === 'porcentaje' ? '1' : '0.01'}
                      min="0"
                      max={formData.tipo_descuento === 'porcentaje' ? '100' : undefined}
                      value={formData.descuento}
                      onChange={handleInputChange}
                      className="bg-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="medico_autoriza_descuento">Autoriza Descuento</Label>
                    <Input
                      id="medico_autoriza_descuento"
                      name="medico_autoriza_descuento"
                      value={formData.medico_autoriza_descuento}
                      onChange={handleInputChange}
                      placeholder="Nombre de quien autoriza"
                      className="bg-white"
                    />
                  </div>
                </>
              )}
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Forma de Pago *</Label>
                <Select
                  value={formData.forma_pago}
                  onValueChange={(value) => handleSelectChange('forma_pago', value)}
                >
                  <SelectTrigger className="bg-white" data-testid="patient-forma-pago">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((method) => (
                      <SelectItem key={method} value={method}>{method}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="no_autorizacion">No. Autorización</Label>
                <Input
                  id="no_autorizacion"
                  name="no_autorizacion"
                  value={formData.no_autorizacion}
                  onChange={handleInputChange}
                  placeholder="Si aplica"
                  className="bg-white"
                />
              </div>
              
              <div className="bg-white rounded-lg p-3 flex items-center justify-between">
                <span className="text-sm text-slate-600">Total a Pagar:</span>
                <span className="text-xl font-bold text-emerald-600" data-testid="patient-total">
                  {formatCurrency(total)}
                </span>
              </div>
            </div>

            {/* Cuenta por Cobrar - Fecha Límite */}
            {formData.forma_pago === 'Cuenta por Cobrar' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-amber-700">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Cuenta por Cobrar</span>
                </div>
                <p className="text-sm text-amber-600">
                  Este paciente será registrado con saldo pendiente de pago.
                </p>
                <div className="space-y-2">
                  <Label htmlFor="fecha_limite_pago">Fecha Límite de Pago</Label>
                  <Input
                    id="fecha_limite_pago"
                    name="fecha_limite_pago"
                    type="date"
                    value={formData.fecha_limite_pago || ''}
                    onChange={handleInputChange}
                    className="bg-white max-w-xs"
                    data-testid="patient-fecha-limite"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Notes Section */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="observaciones">Observaciones</Label>
              <Textarea
                id="observaciones"
                name="observaciones"
                value={formData.observaciones}
                onChange={handleInputChange}
                placeholder="Notas adicionales..."
                className="bg-slate-50 resize-none"
                rows={2}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label htmlFor="enviado" className="font-medium">Marcar como Enviado</Label>
                <p className="text-xs text-slate-500">Indica que el resultado fue enviado al paciente</p>
              </div>
              <Switch
                id="enviado"
                name="enviado"
                checked={formData.enviado}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enviado: checked }))}
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2 pt-4 border-t border-slate-200">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              className="border-slate-300 text-slate-700"
            >
              Cancelar
            </Button>
            <Button 
              type="submit"
              className="bg-[#1a5192] hover:bg-[#0d3a6e] text-white font-semibold"
              data-testid="save-patient-button"
              disabled={saving}
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
              ) : (
                editingPatient ? 'Actualizar Paciente' : 'Guardar Paciente'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PatientForm;
